/**
 * Geo Blocking
 *
 * Two independent blocking mechanisms run in parallel:
 *
 * 1. Country blocking — detects country via Shopify's browsing context
 *    suggestions endpoint. Visitors whose country is not in the allowed
 *    list are blocked.
 *
 * 2. IP blocking — fetches the visitor's public IP via api.ipify.org and
 *    checks it against the admin-configured blocked IP list. Supports exact
 *    IPv4 addresses (e.g. 203.0.113.5) and CIDR ranges (e.g. 192.168.1.0/24).
 *
 * Either check triggering a block is enough to show the block screen.
 * Both checks run concurrently; whichever finishes first and decides to
 * block will lock the page immediately.
 *
 * Configuration is injected from the theme via `window.geoBlockingSettings`:
 *   {
 *     enabled: Boolean,          // master on/off switch
 *     allowedCountries: String,  // e.g. "US,CA,GB"
 *     blockedIps: String         // e.g. "203.0.113.5,192.168.1.0/24"
 *   }
 *
 * Fails open: any detection error leaves the site fully accessible.
 */
(function () {
  'use strict';

  var settings = window.geoBlockingSettings;

  /**
   * Reveal the page (undo the visibility:hidden applied while checking).
   * Safe to call multiple times. Used on every "allow access" path.
   */
  function reveal() {
    if (typeof window.geoBlockingReveal === 'function') {
      window.geoBlockingReveal();
    }
  }

  /**
   * Once the visitor is blocked, strip every <body> child except the
   * geo-blocking screen from the DOM so no page content is ever accessible —
   * not via CSS toggles, not via DevTools, not via console manipulation.
   *
   * Two MutationObservers are started:
   *  1. Watches <html> class — re-adds 'geo-blocked' if ever removed.
   *  2. Watches <body> childList — removes any node injected at runtime
   *     that is not the blocking screen itself.
   *
   * The public reveal/block helpers are neutralised (no-ops) so console
   * calls cannot restore the page.
   */
  function lockBlock() {
    var root = document.documentElement;
    var body = document.body;

    // Remove every body child that is not the geo-blocking screen.
    function purgeBody() {
      var children = Array.prototype.slice.call(body.children);
      for (var i = 0; i < children.length; i++) {
        if (!children[i].classList.contains('geo-blocking-screen')) {
          body.removeChild(children[i]);
        }
      }
    }

    purgeBody();

    // Neutralise the public helpers so console calls are no-ops.
    window.geoBlockingReveal = function () {};
    window.geoBlockingBlock = function () {};

    if (typeof MutationObserver !== 'undefined') {
      // 1. Re-add 'geo-blocked' class if someone strips it via DevTools.
      var classObserver = new MutationObserver(function () {
        if (!root.classList.contains('geo-blocked')) {
          root.classList.add('geo-blocked');
        }
      });
      classObserver.observe(root, { attributes: true, attributeFilter: ['class'] });

      // 2. Remove any node injected into <body> at runtime (e.g. via console).
      var bodyObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType === 1 && !node.classList.contains('geo-blocking-screen')) {
              body.removeChild(node);
            }
          }
        }
      });
      bodyObserver.observe(body, { childList: true });
    }
  }

  /**
   * Show the full-screen block screen instead of the store.
   */
  function block() {
    if (typeof window.geoBlockingBlock === 'function') {
      window.geoBlockingBlock();
    } else {
      // Fallback: if the helper is missing, never keep the page hidden.
      reveal();
      return;
    }
    lockBlock();
  }

  // Nothing to do if config is missing or the feature is turned off.
  if (!settings || !settings.enabled) return reveal();

  // Once a block decision is made, prevent the other check from calling reveal().
  var blocked = false;
  var checksComplete = 0;
  var totalChecks = 0;

  function onCheckComplete(shouldBlock) {
    if (blocked) return;
    if (shouldBlock) {
      blocked = true;
      block();
      return;
    }
    checksComplete++;
    if (checksComplete === totalChecks) {
      reveal();
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Parse a comma-separated string into a trimmed, non-empty array.
   * @param {string} raw
   * @returns {string[]}
   */
  function parseList(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length > 0; });
  }

  // ─── IP Blocking ────────────────────────────────────────────────────────────

  /**
   * Convert a dotted IPv4 string to a 32-bit unsigned integer.
   * @param {string} ip
   * @returns {number}
   */
  function ipToInt(ip) {
    return ip.split('.').reduce(function (acc, octet) {
      return (acc << 8) + parseInt(octet, 10);
    }, 0) >>> 0;
  }

  /**
   * Check whether a visitor IP matches a single entry which may be either
   * an exact address ("203.0.113.5") or a CIDR range ("192.168.1.0/24").
   * @param {string} visitorIp
   * @param {string} entry
   * @returns {boolean}
   */
  function ipMatchesEntry(visitorIp, entry) {
    if (entry.indexOf('/') !== -1) {
      var parts = entry.split('/');
      var networkAddr = parts[0];
      var prefixLen = parseInt(parts[1], 10);
      if (isNaN(prefixLen) || prefixLen < 0 || prefixLen > 32) return false;
      var mask = prefixLen === 0 ? 0 : (~0 << (32 - prefixLen)) >>> 0;
      return (ipToInt(visitorIp) & mask) === (ipToInt(networkAddr) & mask);
    }
    return visitorIp === entry;
  }

  /**
   * Return true if visitorIp matches any entry in the blocked list.
   * @param {string} visitorIp
   * @param {string[]} blockedList
   * @returns {boolean}
   */
  function isIpBlocked(visitorIp, blockedList) {
    for (var i = 0; i < blockedList.length; i++) {
      if (ipMatchesEntry(visitorIp, blockedList[i])) return true;
    }
    return false;
  }

  /**
   * Fetch the visitor's public IP from api.ipify.org.
   * @returns {Promise<string|null>}
   */
  function detectIp() {
    return fetch('https://api.ipify.org?format=json', {
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('IP lookup failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        return data && data.ip ? String(data.ip).trim() : null;
      });
  }

  // ─── Country Blocking ───────────────────────────────────────────────────────

  /**
   * Fetch the visitor's detected country code from Shopify's endpoint.
   * @returns {Promise<string|null>} Uppercase country code, or null if unknown.
   */
  function detectCountry() {
    return fetch('/browsing_context_suggestions.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Geo lookup failed: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var country =
          data &&
          data.detected_values &&
          data.detected_values.country &&
          data.detected_values.country.handle;
        return country ? String(country).toUpperCase() : null;
      });
  }

  // ─── Run checks ─────────────────────────────────────────────────────────────

  var allowedCountries = parseList(settings.allowedCountries).map(function (c) {
    return c.toUpperCase();
  });
  var blockedIps = parseList(settings.blockedIps);

  // Count how many checks are actually active so reveal() fires at the right time.
  if (allowedCountries.length > 0) totalChecks++;
  if (blockedIps.length > 0) totalChecks++;

  // If neither check is configured, fail open.
  if (totalChecks === 0) return reveal();

  if (allowedCountries.length > 0) {
    detectCountry()
      .then(function (country) {
        if (!country) { onCheckComplete(false); return; }
        onCheckComplete(allowedCountries.indexOf(country) === -1);
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[Geo Blocking] Country detection error, allowing access:', err);
        }
        onCheckComplete(false);
      });
  }

  if (blockedIps.length > 0) {
    detectIp()
      .then(function (ip) {
        if (!ip) { onCheckComplete(false); return; }
        onCheckComplete(isIpBlocked(ip, blockedIps));
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[Geo Blocking] IP detection error, allowing access:', err);
        }
        onCheckComplete(false);
      });
  }
})();
